/**
 * Collection circles (JEW-62).
 *
 * The section is CSS-only for layout. This adds exactly two behaviours that CSS
 * cannot express:
 *
 * 1. Edge fades that appear only on real overflow, so a destination set that
 *    already fits never shows a fake "there is more" cue.
 * 2. Bringing the active circle into view inside the scroller on load, so the
 *    L3 active state is still visible on mobile when the current collection sits
 *    past the fold of the row.
 *
 * Both are attribute toggles; all painting stays in the section stylesheet.
 */
class CollectionCirclesComponent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.collection-circles__track');
    if (!this.track) return;

    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', this.update, { passive: true });

    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);

    this.revealActive();
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.track?.removeEventListener('scroll', this.update);
  }

  /**
   * Scrolls the current destination into the visible part of the row. Deliberately
   * assigns scrollLeft instead of calling scrollIntoView(), which would also
   * scroll the page and move the row under the visitor on load.
   */
  revealActive() {
    const active = this.track.querySelector('[aria-current="page"]');
    if (!active) return;

    const overhangRight = active.offsetLeft + active.offsetWidth - (this.track.scrollLeft + this.track.clientWidth);
    const overhangLeft = this.track.scrollLeft - active.offsetLeft;

    if (overhangRight > 0) {
      this.track.scrollLeft += overhangRight + 16;
    } else if (overhangLeft > 0) {
      this.track.scrollLeft -= overhangLeft + 16;
    }
  }

  update() {
    // 1px tolerance: sub-pixel layout rounding otherwise reports a permanent
    // one-pixel overflow and the fade would never switch off.
    const overflows = this.track.scrollWidth - this.track.clientWidth > 1;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;

    this.toggleAttribute('fade-start', overflows && !atStart);
    this.toggleAttribute('fade-end', overflows && !atEnd);
  }
}

if (!customElements.get('collection-circles-component')) {
  customElements.define('collection-circles-component', CollectionCirclesComponent);
}
